import { cn } from "./cn";

// ── Progress ─────────────────────────────────────────────────────────────────

interface ProgressProps {
  /** 0-100 */
  value: number;
  /** Indeterminate mode (animated stripe). */
  indeterminate?: boolean;
  variant?: "default" | "success" | "warning" | "error";
  className?: string;
}

const variantColors: Record<NonNullable<ProgressProps["variant"]>, string> = {
  default: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  error: "bg-destructive",
};

/**
 * Linear progress bar.
 *
 * ```tsx
 * <Progress value={60} />
 * <Progress indeterminate />
 * ```
 */
export function Progress({
  value,
  indeterminate = false,
  variant = "default",
  className,
}: ProgressProps) {
  const barColor = variantColors[variant];
  const clamped = Math.max(0, Math.min(100, value));

  return (
    <div
      role="progressbar"
      aria-valuenow={indeterminate ? undefined : clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn(
        "h-2 w-full overflow-hidden rounded-full bg-muted",
        className
      )}
    >
      <div
        className={cn(
          "h-full rounded-full transition-all duration-300",
          barColor,
          indeterminate && "animate-indeterminate w-1/3"
        )}
        style={indeterminate ? undefined : { width: `${clamped}%` }}
      />
    </div>
  );
}
